<template>
  <div class="min-h-screen">
    <Header />
    
    <!-- Product Hero -->
    <section class="relative pt-32 pb-20 overflow-hidden">
      <div class="absolute inset-0 space-pattern"></div>
      <div class="absolute top-0 right-1/4 w-96 h-96 bg-gradient-to-br from-cosmic/20 to-nebula/20 rounded-full blur-3xl"></div>

      <div class="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div class="inline-flex items-center px-4 py-2 rounded-full bg-cosmic-50 dark:bg-white/5 border border-primary-20 dark:border-white/10 mb-6">
              <FeatureIcon :name="productIconName" size="md" variant="minimal" class="mr-2" />
              <span class="text-cosmic-adaptive text-sm font-medium">{{ product.subtitle }}</span>
            </div>

            <h1 class="text-5xl md:text-6xl font-bold mb-6 text-gray-900 dark:text-white">{{ product.title }}</h1>
            <p class="text-xl text-gray-700 dark:text-dark-muted mb-8">{{ product.description }}</p>

            <div class="flex flex-wrap gap-3 mb-8">
              <span v-for="tag in product.tags" :key="tag"
                    class="px-4 py-2 bg-primary-50 dark:bg-white/5 border border-primary-20 dark:border-white/10 rounded-full text-sm text-primary dark:text-cosmic-adaptive">
                {{ tag }}
              </span>
            </div>

            <div class="flex flex-col sm:flex-row gap-4">
              <button @click="scrollToAboutContact" class="btn-primary">立即体验</button>
              <button @click="scrollToAboutContact" class="btn-secondary">了解详情</button>
            </div>
          </div>

          <div class="relative">
            <div class="glass-card-enhanced p-8 border border-primary-20 dark:border-white/10">
              <div class="flex justify-center mb-6">
                <div class="w-32 h-32 icon-shell icon-shell-strong animate-float">
                  <FeatureIcon :name="productIconName" size="xl" variant="minimal" />
                </div>
              </div>
              <div v-if="product.stats && product.stats.length >= 4" class="grid grid-cols-2 gap-4">
                <div class="bg-primary-50 dark:bg-white/5 rounded-lg p-4 text-center">
                  <div class="text-2xl font-bold gradient-text-animated">{{ product.stats[0]?.value }}</div>
                  <div class="text-xs text-gray-700 dark:text-dark-muted mt-1">{{ product.stats[0]?.label }}</div>
                </div>
                <div class="bg-primary-50 dark:bg-white/5 rounded-lg p-4 text-center">
                  <div class="text-2xl font-bold gradient-text-animated">{{ product.stats[1]?.value }}</div>
                  <div class="text-xs text-gray-700 dark:text-dark-muted mt-1">{{ product.stats[1]?.label }}</div>
                </div>
                <div class="bg-primary-50 dark:bg-white/5 rounded-lg p-4 text-center">
                  <div class="text-2xl font-bold gradient-text-animated">{{ product.stats[2]?.value }}</div>
                  <div class="text-xs text-gray-700 dark:text-dark-muted mt-1">{{ product.stats[2]?.label }}</div>
                </div>
                <div class="bg-primary-50 dark:bg-white/5 rounded-lg p-4 text-center">
                  <div class="text-2xl font-bold gradient-text-animated">{{ product.stats[3]?.value }}</div>
                  <div class="text-xs text-gray-700 dark:text-dark-muted mt-1">{{ product.stats[3]?.label }}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Features Section -->
    <section class="py-20">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 class="section-title gradient-text-animated mb-16">核心功能</h2>

        <div class="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          <div v-for="(feature, index) in product.features" :key="index"
               class="card card-hover">
            <div class="w-14 h-14 icon-shell mb-4">
              <FeatureIcon :name="feature.iconName || 'targeting'" size="lg" variant="minimal" />
            </div>
            <h3 class="text-xl font-bold mb-3 text-gray-900 dark:text-white">{{ feature.title }}</h3>
            <p class="text-gray-700 dark:text-dark-muted text-sm">{{ feature.description }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Benefits Section -->
    <section class="py-20 relative">
      <div class="absolute inset-0 bg-gradient-to-r from-cosmic/5 via-nebula/5 to-stellar/5"></div>

      <div class="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <h2 class="section-title gradient-text mb-8">为什么选择我们</h2>

            <div class="space-y-6">
              <div v-for="(benefit, index) in product.benefits" :key="index"
                   class="flex items-start">
                <div class="flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br from-cosmic to-nebula flex items-center justify-center mr-4">
                  <svg class="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"></path>
                  </svg>
                </div>
                <div>
                  <h3 class="text-xl font-bold mb-2 text-gray-900 dark:text-white">{{ benefit.title }}</h3>
                  <p class="text-gray-700 dark:text-dark-muted">{{ benefit.description }}</p>
                </div>
              </div>
            </div>
          </div>

          <div class="glass-card p-8 border border-primary-20 dark:border-white/10 product-tech-surface">
            <h3 class="text-2xl font-bold mb-6 text-gray-900 dark:text-white">技术优势</h3>
            <div class="space-y-4">
              <div v-for="(tech, index) in product.techSpecs" :key="index"
                   class="flex justify-between items-center p-4 rounded-lg product-tech-item">
                <span class="text-gray-700 dark:text-dark-muted">{{ tech.label }}</span>
                <span class="text-cosmic-adaptive font-semibold">{{ tech.value }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- DMP Capabilities Section (DSP only) -->
    <section v-if="slug === 'dsp' && product.dmpCapabilities" class="py-20">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="text-center mb-16">
          <div class="inline-flex items-center justify-center w-20 h-20 rounded-2xl icon-shell icon-shell-strong mb-6">
            <FeatureIcon name="data" size="xl" variant="minimal" />
          </div>
          <h2 class="section-title gradient-text mb-4">{{ product.dmpCapabilities.title }}</h2>
          <p class="text-gray-700 dark:text-dark-muted max-w-2xl mx-auto">
            {{ product.dmpCapabilities.description }}
          </p>
        </div>

        <!-- DMP Stats -->
        <div v-if="product.dmpCapabilities.stats" class="grid grid-cols-2 md:grid-cols-4 gap-6 mb-16">
          <div v-for="(stat, index) in product.dmpCapabilities.stats" :key="index"
               class="text-center">
            <div class="text-3xl md:text-4xl font-bold gradient-text mb-2">{{ stat.value }}</div>
            <div class="text-gray-700 dark:text-dark-muted">{{ stat.label }}</div>
          </div>
        </div>

        <!-- DMP Features -->
        <div class="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div v-for="(feature, index) in product.dmpCapabilities.features" :key="index"
               class="card card-hover p-6">
            <div class="flex items-center mb-3">
              <div class="flex-shrink-0 w-12 h-12 rounded-xl icon-shell mr-3">
                <FeatureIcon :name="feature.iconName || 'data'" size="md" variant="minimal" />
              </div>
              <h3 class="text-lg font-bold text-gray-900 dark:text-white">{{ feature.title }}</h3>
            </div>
            <p class="text-gray-700 dark:text-dark-muted text-sm leading-relaxed">{{ feature.description }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Certifications Section (SDK only) -->
    <section v-if="product.certifications && product.certifications.length" class="py-20">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 class="section-title gradient-text mb-16 text-center">权威认证</h2>
        <p class="text-center text-gray-700 dark:text-dark-muted mb-12 max-w-3xl mx-auto">
          通过多项国家级权威机构安全专项检验，确保SDK安全合规
        </p>

        <div class="grid md:grid-cols-3 gap-6">
          <div v-for="(cert, index) in product.certifications" :key="index"
               class="card card-hover group">
            <div class="flex flex-col items-center text-center p-4">
              <!-- Certificate Thumbnail Preview -->
              <div class="w-48 aspect-[3/4] rounded-lg mb-4 overflow-hidden border border-primary-10 dark:border-white/10 group-hover:border-primary-20 dark:group-hover:border-white/20 transition-all duration-300 shadow-sm group-hover:shadow-md">
                <img :src="cert.thumbnail" :alt="cert.name" class="w-full h-full object-cover">
              </div>

              <h3 class="text-sm font-bold mb-3 text-gray-900 dark:text-white px-2 line-clamp-2">{{ cert.name }}</h3>
              <a :href="cert.file" target="_blank" rel="noopener noreferrer"
                 class="inline-flex items-center px-5 py-2 bg-primary-50 dark:bg-white/5 border border-primary-20 dark:border-white/10 rounded-full text-sm text-primary dark:text-cosmic-adaptive hover:bg-primary-100 dark:hover:bg-white/10 transition-colors duration-300">
                <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                </svg>
                查看证书
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- CTA Section -->
    <section class="py-32">
      <div class="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <h2 class="text-4xl md:text-5xl font-bold mb-6 gradient-text">开始使用{{ product.title }}</h2>
        <p class="text-xl text-gray-700 dark:text-dark-muted mb-12">
          立即体验{{ product.title }}的强大功能，开启智能营销新篇章
        </p>
        
        <div class="flex flex-col sm:flex-row gap-6 justify-center">
          <button @click="scrollToAboutContact" class="btn-primary">免费试用</button>
          <button @click="scrollToAboutContact" class="btn-secondary">联系销售</button>
        </div>
      </div>
    </section>

    <Footer />
  </div>
</template>

<script setup>
const { slug } = useRoute().params

const products = {
  ssp: {
    icon: '📺',
    title: 'MediaGet SSP',
    subtitle: '媒体管理平台',
    description: '智能化的媒体资源管理系统，帮助媒体方最大化收益，实现流量价值最大化',
    tags: ['SSP', '媒体变现', '实时竞价', '收益优化'],
    stats: [
      { value: '300+', label: '合作媒体' },
      { value: '30%+', label: '填充率' },
      { value: '+45%', label: '收益提升' },
      { value: '<100ms', label: '响应时间P95' }
    ],
    features: [
      { icon: '⚡', iconName: 'rtb', title: '实时竞价', description: '支持OpenRTB协议，毫秒级竞价响应' },
      { icon: '📊', iconName: 'pricing', title: '智能定价', description: 'AI驱动的动态定价算法，最大化收益' },
      { icon: '🎯', iconName: 'targeting', title: '精准投放', description: '多维度用户标签，提升广告匹配度' },
      { icon: '📈', iconName: 'analytics', title: '数据分析', description: '实时数据监控，深度分析报告' },
      { icon: '🔒', iconName: 'security', title: '安全防护', description: '多重安全验证，保护媒体利益' },
      { icon: '🌐', iconName: 'global', title: '全球覆盖', description: '支持国内外主流广告平台' }
    ],
    benefits: [
      { title: '收益最大化', description: '智能算法优化，确保每次展示获得最佳收益' },
      { title: '操作简便', description: '简洁的管理界面，轻松配置和管理广告位' },
      { title: '透明公开', description: '实时数据展示，收益明细一目了然' },
      { title: '专业支持', description: '7x24小时技术支持，确保稳定运行' }
    ],
    techSpecs: [
      { label: '日处理请求', value: '300亿+' },
      { label: '超时率', value: '<1%' },
      { label: '处理能力(峰值QPS)', value: '200万' },
      { label: '系统可用性', value: '99.99%' }
    ]
  },
  dsp: {
    icon: '🎯',
    title: 'Adget DSP',
    subtitle: '广告管理平台',
    description: '精准的广告投放系统，实现跨媒体的智能投放，帮助广告主精准触达目标用户',
    tags: ['DSP', '程序化购买', '精准投放', '效果优化'],
    stats: [
      { value: '10亿+', label: '触达用户' },
      { value: '98.5%', label: '投放准确率' },
      { value: '+156%', label: 'ROI提升' },
      { value: '<50ms', label: '响应时间' }
    ],
    features: [
      { icon: '🎯', iconName: 'targeting', title: '精准定向', description: '基于DMP数据的用户画像，精准定向' },
      { icon: '🤖', iconName: 'ai', title: '智能投放', description: 'AI算法自动优化，提升投放效果' },
      { icon: '📊', iconName: 'monitor', title: '实时监控', description: '投放数据实时监控，及时调整策略' },
      { icon: '🔄', iconName: 'cross-screen', title: '跨屏投放', description: '支持移动、PC、OTT等多屏投放' },
      { icon: '📈', iconName: 'analytics', title: '效果分析', description: '深度数据分析，提供优化建议' },
      { icon: '💰', iconName: 'budget', title: '预算控制', description: '灵活的预算管理，控制投放成本' }
    ],
    benefits: [
      { title: '精准获客', description: '大数据用户画像，精准触达目标客户' },
      { title: '效果保证', description: '智能算法优化，确保投放效果' },
      { title: '成本可控', description: '灵活的竞价策略，控制获客成本' },
      { title: '全渠道覆盖', description: '整合主流媒体资源，一站式投放' }
    ],
    techSpecs: [
      { label: '覆盖包名', value: '10万+' },
      { label: '日曝光量', value: '1000万+' },
      { label: '用户标签', value: '10000+' },
      { label: '算法模型', value: 'AI驱动' }
    ],
    dmpCapabilities: {
      title: 'DMP数据能力',
      description: '集成DataGet DMP大数据管理平台，提供强大的数据支撑',
      icon: '📊',
      stats: [
        { value: '10亿+', label: '用户数据' },
        { value: '10000+', label: '用户标签' },
        { value: 'TB级', label: '数据处理' },
        { value: '98%', label: '准确率' }
      ],
      features: [
        { icon: '📊', iconName: 'data', title: '数据整合', description: '整合多方数据源，构建统一数据平台' },
        { icon: '👤', iconName: 'users', title: '用户画像', description: '多维度用户标签，精准刻画用户特征' },
        { icon: '🔍', iconName: 'analytics', title: '深度分析', description: '机器学习算法，深度挖掘数据价值' },
        { icon: '📈', iconName: 'growth', title: '预测分析', description: '预测用户行为，指导营销决策' },
        { icon: '🔄', iconName: 'integration', title: '数据同步', description: '实时数据更新，保持数据新鲜度' },
        { icon: '🔒', iconName: 'security', title: '数据安全', description: '企业级安全防护，保护数据隐私' }
      ],
      benefits: [
        { title: '数据驱动', description: '基于大数据的用户画像，精准触达目标用户' },
        { title: '智能优化', description: 'AI算法自动优化投放策略，提升ROI' },
        { title: '实时同步', description: '数据实时更新，保持投放策略最新' }
      ]
    }
  },
  // DMP产品已注释 - 功能已集成到DSP
  // dmp: {
  //   icon: '📊',
  //   title: 'DataGet DMP',
  //   subtitle: '大数据管理平台',
  //   description: '强大的数据管理平台，深度挖掘数据价值，构建完整的用户数据资产',
  //   tags: ['DMP', '数据管理', '用户画像', '数据分析'],
  //   stats: [
  //     { value: '10亿+', label: '用户数据' },
  //     { value: '10000+', label: '用户标签' },
  //     { value: 'TB级', label: '数据处理' },
  //     { value: '98%', label: '准确率' }
  //   ],
  //   features: [
  //     { icon: '📊', title: '数据整合', description: '整合多方数据源，构建统一数据平台' },
  //     { icon: '👤', title: '用户画像', description: '多维度用户标签，精准刻画用户特征' },
  //     { icon: '🔍', title: '深度分析', description: '机器学习算法，深度挖掘数据价值' },
  //     { icon: '📈', title: '预测分析', description: '预测用户行为，指导营销决策' },
  //     { icon: '🔄', title: '数据同步', description: '实时数据更新，保持数据新鲜度' },
  //     { icon: '🔒', title: '数据安全', description: '企业级安全防护，保护数据隐私' }
  //   ],
  //   benefits: [
  //     { title: '数据资产化', description: '将数据转化为可应用的用户资产' },
  //     { title: '精准营销', description: '基于用户画像的精准营销' },
  //     { title: '决策支持', description: '数据分析指导业务决策' },
  //     { title: '合规安全', description: '符合数据保护法规，安全可靠' }
  //   ],
  //   techSpecs: [
  //     { label: '数据规模', value: '10亿+用户' },
  //     { label: '标签维度', value: '10000+标签' },
  //     { label: '处理能力', value: 'TB级/天' },
  //     { label: '准确率', value: '98%+' }
  //   ]
  // },
  adx: {
    icon: '🌍',
    title: '海外ADX',
    subtitle: '海外广告交易平台',
    description: '连接全球广告资源，拓展国际市场，助力企业出海',
    tags: ['ADX', '海外投放', '跨境营销', '全球化'],
    stats: [
      { value: '200+', label: '覆盖国家' },
      { value: '10000+', label: '合作媒体' },
      { value: '500万+', label: '日曝光量' },
      { value: '24/7', label: '技术支持' }
    ],
    features: [
      { icon: '🌍', iconName: 'global', title: '全球覆盖', description: '覆盖200+国家和地区，全球媒体资源' },
      { icon: '🎯', iconName: 'targeting', title: '本地化投放', description: '支持多语言、多币种本地化运营' },
      { icon: '📊', iconName: 'data', title: '跨境数据', description: '全球用户数据，精准海外定向' },
      { icon: '💰', iconName: 'money', title: '灵活结算', description: '支持多种结算方式，便捷跨境支付' },
      { icon: '🛡️', iconName: 'security', title: '合规保障', description: '了解各国法规，确保投放合规' },
      { icon: '📈', iconName: 'growth', title: '效果优化', description: '本地化优化策略，提升投放效果' }
    ],
    benefits: [
      { title: '全球触达', description: '覆盖全球主要市场，触达海外用户' },
      { title: '本地化运营', description: '专业的本地化团队，提供本土化服务' },
      { title: '合规保障', description: '熟悉各国法规，确保合规投放' },
      { title: '效果优化', description: '针对海外市场优化的投放策略' }
    ],
    techSpecs: [
      { label: '覆盖国家', value: '200+' },
      { label: '海外媒体', value: '10000+' },
      { label: '日曝光量', value: '500万+' },
      { label: '支持语言', value: '10+' }
    ]
  },
  sdk: {
    icon: '📱',
    title: 'MagicAd SDK',
    subtitle: '移动广告SDK',
    description: '安全合规的移动广告变现SDK，支持Android/iOS双端集成，通过多项权威安全认证',
    tags: ['SDK', '移动广告', '安全认证', '双端支持'],
    stats: [
      { value: '50+', label: '接入应用' },
      { value: '99.9%', label: '稳定性' },
      { value: '3项', label: '权威认证' },
      { value: '70%+', label: '填充率' }
    ],
    features: [
      { icon: '🔒', iconName: 'security', title: '安全认证', description: '通过国家权威机构安全专项检验' },
      { icon: '📱', iconName: 'integration', title: '双端支持', description: '完美支持Android和iOS双平台' },
      { icon: '💰', iconName: 'growth', title: '高收益', description: '智能竞价算法，最大化广告收益' },
      { icon: '⚡', iconName: 'speed', title: '轻量集成', description: 'SDK体积小，接入简单快速' },
      { icon: '📊', iconName: 'data', title: '数据分析', description: '完善的数据报表，收益透明可查' },
      { icon: '🛡️', iconName: 'security', title: '隐私合规', description: '符合国内外隐私保护法规要求' }
    ],
    benefits: [
      { title: '权威认证', description: '获得国家级安全专项检验证书，安全可靠' },
      { title: '简单易用', description: '提供完善的接入文档和示例代码，快速集成' },
      { title: '收益保障', description: '对接主流广告源，智能优化eCPM' },
      { title: '技术支持', description: '专业技术团队，全程接入指导' }
    ],
    techSpecs: [
      { label: '安卓-sdk体积', value: '1.2MB' },
      { label: 'ios-sdk体积', value: '4.5MB' },
      { label: '崩溃率', value: '<0.01%' },
      { label: '广告响应速度', value: '<500ms' }
    ],
    certifications: [
      { name: 'SDK安全专项检验证书 - Android端', file: '/cert/SDK安全专项检验证书_MagicAdSDK安卓端(CA).pdf', thumbnail: '/cert/SDK安全专项检验证书_MagicAdSDK安卓端_thumb.webp' },
      { name: 'SDK安全专项检验证书 - iOS端', file: '/cert/SDK安全专项检验证书-MagicAdSDK_iOS端(CA).pdf', thumbnail: '/cert/SDK安全专项检验证书-MagicAdSDK_iOS端_thumb.webp' },
      { name: 'ISO37301合规管理体系认证证书', file: '/cert/03025CMS10093R0S-上海咩叽科网络科技有限公司-ISO37301证书.pdf', thumbnail: '/cert/03025CMS10093R0S-上海咩叽科网络科技有限公司-ISO37301证书_thumb.webp' }
    ]
  },
  account: {
    icon: '⚡',
    title: '开户投流',
    subtitle: '一站式开户服务',
    description: '快速开通各类广告账户，启动专业投放服务',
    tags: ['开户服务', '广告代运营', '效果投放'],
    stats: [
      { value: '50+', label: '服务客户' },
      { value: '1小时', label: '开户速度' },
      { value: '300+', label: '每日素材产出' },
      { value: '24/7', label: '客户服务' }
    ],
    features: [
      { icon: '⚡', iconName: 'speed', title: '快速开户', description: '简化开户流程，1小时内完成开户' },
      { icon: '🎯', iconName: 'targeting', title: '专业代运营', description: '资深投放团队，专业代运营服务' },
      { icon: '📊', iconName: 'data', title: '效果保证', description: '数据驱动优化，确保投放效果' },
      { icon: '💰', iconName: 'budget', title: '成本控制', description: '智能竞价策略，控制投放成本' },
      { icon: '📈', iconName: 'analytics', title: '数据分析', description: '深度数据分析，提供优化建议' },
      { icon: '🔄', iconName: 'integration', title: '账户管理', description: '一站式账户管理，简化操作流程' }
    ],
    benefits: [
      { title: '省时高效', description: '专业团队代为操作，节省时间成本' },
      { title: '效果保证', description: '经验丰富的投放团队，确保效果' },
      { title: '成本透明', description: '清晰的费用结构，无隐藏费用' },
      { title: '专业服务', description: '一对一专属客户经理服务' }
    ],
    techSpecs: [
      { label: '服务客户', value: '50+' },
      { label: '开户时效', value: '1小时' },
      { label: '开户平台', value: '20+' },
      { label: '客户满意度', value: '99%' }
    ]
  },
  'private-domain': {
    icon: '💎',
    title: '私域流量',
    subtitle: '私域运营解决方案',
    description: '构建企业私域流量池，提升客户终身价值',
    tags: ['私域流量', '用户运营', '客户关系管理'],
    stats: [
      { value: '500万+', label: '服务用户' },
      { value: '+234%', label: '转化提升' },
      { value: '98%', label: '客户满意度' },
      { value: '3倍', label: '复购提升' }
    ],
    features: [
      { icon: '👥', iconName: 'users', title: '用户沉淀', description: '多渠道用户导入，构建私域流量池' },
      { icon: '🎯', iconName: 'targeting', title: '精准营销', description: '基于用户画像的精准营销推送' },
      { icon: '🔄', iconName: 'lifecycle', title: '生命周期管理', description: '完整的用户生命周期管理' },
      { icon: '📊', iconName: 'analytics', title: '数据分析', description: '用户行为分析，优化运营策略' },
      { icon: '💬', iconName: 'chat', title: '智能客服', description: 'AI智能客服，提升用户体验' },
      { icon: '🎁', iconName: 'member', title: '会员体系', description: '完善的会员积分体系，提升用户粘性' }
    ],
    benefits: [
      { title: '流量私有化', description: '构建自有流量池，不再依赖平台' },
      { title: '成本降低', description: '私域运营成本远低于公域获客' },
      { title: '转化提升', description: '精准营销提升转化率' },
      { title: '用户粘性', description: '深度运营提升用户忠诚度' }
    ],
    techSpecs: [
      { label: '服务用户', value: '500万+' },
      { label: '转化提升', value: '+234%' },
      { label: '复购提升', value: '3倍' },
      { label: '客户满意度', value: '98%' }
    ]
  }
}

const product = computed(() => {
  return products[slug] || products['ssp']
})

// 产品图标名称映射
const productIconName = computed(() => {
  const iconMap = {
    'ssp': 'ssp',
    'dsp': 'dsp',
    'sdk': 'sdk',
    'adx': 'global',
    'account': 'speed',
    'private-domain': 'private-domain'
  }
  return iconMap[slug] || 'targeting'
})

// 设置页面meta
useHead({
  title: product.value?.title || '产品详情',
  meta: [
    { name: 'description', content: product.value?.description }
  ]
})

const scrollToAboutContact = () => {
  window.location.href = '/about#contact'
}

</script>
